import { HoverCard, LoadingOverlay, Text } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { motion } from "framer-motion"
import { User, UserCircle, Users } from "lucide-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useParams } from "react-router-dom"
import {
  CollectionQuery,
  Filter,
  Order,
} from "../../../models/collection.model"
import {
  EntityConfig,
  entityViewMode,
} from "../../../models/entity-config.model"
import { Owner } from "../../../models/owner.model"
import EntityList from "../../../shared/component/entity-list/entity-list.component"
import ExportModalComponent from "../../../shared/component/export-modal/component/export-modal.component"
import { useCurrentUser } from "../../../shared/hooks/current-user.hook"
import { dateFormat } from "../../../shared/utility/date-format/date-format"
import StatCard from "../../dashboard/component/stat-cart"
import { exportOwners } from "../api/owner.api"
import {
  useBulkToggleOwnerStatusMutation,
  useLazyCountOwnersQuery,
  useLazyGetOwnersQuery,
} from "../store/owner.query"

export default function OwnerPage() {
  const params = useParams()
  const location = useLocation()
  const { t, i18n } = useTranslation()
  const [viewMode, setViewMode] = useState<entityViewMode>("list")
  const [collection, setCollection] = useState<CollectionQuery>({
    skip: 0,
    top: 10,
    orderBy: [{ field: "createdAt", direction: "desc" }],
    // includes: ["createdBy"]
  })

  const [getOwners, owners] = useLazyGetOwnersQuery()
  const [openedExport, { open: openExport, close: closeExport }] =
    useDisclosure()
  const [countOwners, ownerCount] = useLazyCountOwnersQuery()
  const [countActiveOwners, activeOwnerCount] = useLazyCountOwnersQuery()
  const [countInactiveOwners, inactiveOwnerCount] = useLazyCountOwnersQuery()
  const [bulkToggleStatus, bulkToggleStatusResponse] =
    useBulkToggleOwnerStatusMutation()
  const [userInfo, userId, ownerInfo] = useCurrentUser()
  const generateFilterOption = (value: boolean): Filter[][] => {
    if (userInfo.role.key == "super_admin") {
      return [
        [{ field: "enabled", value: value, operator: "=" }],
        // [{ field: "parentOwnerId", value: value, operator: "=" }],
      ]
    }
    if (userInfo.role.key == "sales") {
      return [
        [{ field: "enabled", value: value, operator: "=" }],
        [{ field: "createdBy", value: userId, operator: "=" }],
      ]
    }
    if (userInfo.role.key == "owner") {
      return [
        [{ field: "enabled", value: value, operator: "=" }],
        [
          {
            field: "parentOwnerId",
            value: ownerInfo?.parentOwnerId,
            operator: "=",
          },
        ],
      ]
    }

    return [
      [
        { field: "enabled", value: value, operator: "=" },
        { field: "parentOwnerId", value: userId, operator: "=" },
      ],
    ]
  }
  const generateFilterOptionToGet = (): Filter[][] => {
    if (userInfo.role.key == "super_admin") {
      return []
    }
    if (userInfo.role.key == "sales") {
      return [[{ field: "createdBy", value: userId, operator: "=" }]]
    }
    if (userInfo.role.key == "owner") {
      return [
        [
          {
            field: "parentOwnerId",
            value: ownerInfo?.parentOwnerId,
            operator: "=",
          },
        ],
      ]
    }

    return [[{ field: "parentOwnerId", value: userId, operator: "=" }]]
  }
  // const generateFilterOptionForAll = (value: boolean): Filter[][] => {

  //   if (userInfo.role.key == "super_admin") {
  //     return [[{ field: "enabled", value: value, operator: "=" }]]
  //   }
  //   if (userInfo.role.key == "sales") {
  //     return [
  //       [{ field: "enabled", value: value, operator: "=" }],
  //       [{ field: "createdBy", value: userId, operator: "=" }],
  //     ]
  //   }
  //   if (userInfo.role.key == "owner") {
  //     return [
  //       [{ field: "enabled", value: value, operator: "=" }],
  //       [{ field: "parentOwnerId", value: ownerInfo?.parentOwnerId, operator: "=" }],
  //     ]
  //   }

  //   return [
  //     [
  //       { field: "enabled", value: value, operator: "=" },
  //       { field: "ownerId", value: userId, operator: "=" },
  //     ],
  //   ]
  // }
  useEffect(() => {
    getOwners({ ...collection, filter: generateFilterOptionToGet() })
  }, [collection])

  useEffect(() => {
    if (params?.id !== undefined) {
      setViewMode("detail")
    } else {
      setViewMode("list")
    }
    if (location.pathname === "/owner/archived") {
      setViewMode("detail")
    }
  }, [setViewMode, params?.id, location])

  useEffect(() => {
    // if(userInfo.role.key == 'super_admin'){
    countOwners({ count: true, filter: generateFilterOption(true) })
    // }else{
    //   countOwners({count:true, filter: [[{value: userId, operator: '=', field: 'ownerId'}]]});
    // }

    countInactiveOwners({ count: true, filter: generateFilterOption(false) })
    countActiveOwners({ count: true, filter: generateFilterOption(true) })
  }, [])

  const data: Owner[] | undefined = owners.data?.data
  const config: EntityConfig<Owner> = {
    primaryColumn: {
      key: "name",
      name: t("name"),
      render(value) {
        return (
          <>
            <img
              src={value?.gender === "male" ? "/male.svg" : "/female.svg"}
              alt="Product Image"
              className="size-8 rounded-full"
            />
            {value.name}
          </>
        )
      },
    },
    detailUrl: "detail",
    rootUrl: "/owner",
    identity: "id",
    name: "name",
    visibleColumn: [
      {
        key: "names",
        name: t("name"),
        render: (owner) => {
          return (
            <Text size="sm" className={owner?.deletedAt ? "text-danger" : ""}>
              {owner?.name}{" "}
              {owner?.isMaintenance && (
                <span className="bg-red-100 text-blue-300 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full dark:bg-blue-300 dark:text-black">
                  {t("maintenance")}
                </span>
              )}
            </Text>
          )
        },
      },
      {
        key: "email",
        name: t("email"),
        render(owner) {
          return owner?.email
        },
      },
      {
        key: "phoneNumber",
        name: t("phoneNumber"),
        render(owner) {
          return owner?.phoneNumber
        },
      },
      {
        key: "pricePerMonth",
        name: t("pricePerMonth"),
        render(owner: Owner) {
          return owner?.pricePerMonth
        },
      },
      {
        key: "gender",
        name: t("gender"),
        render: (owner: Owner) => {
          return owner?.gender?.toLowerCase() === "male" ? "Male" : "Female"
        },
      },
      // {
      //   key: "archiveReason",
      //   name: t("archiveReason"),
      //   hideSort: true,
      //   render: (owner: Owner) => {
      //     if (owner?.archiveReason) {
      //       return (
      //         <HoverCard width={280} shadow="md">
      //           <HoverCard.Target>
      //             <Text size="sm" className={"cursor-pointer"}>
      //               {owner?.archiveReason && owner?.archiveReason?.length > 25
      //                 ? owner?.archiveReason?.substring(0, 22) + "..."
      //                 : owner?.archiveReason}
      //             </Text>
      //           </HoverCard.Target>
      //           <HoverCard.Dropdown
      //             className={
      //               "text-justify break-all wrap max-h-60 overflow-auto"
      //             }
      //           >
      //             <Text size="sm">{owner?.archiveReason}</Text>
      //           </HoverCard.Dropdown>
      //         </HoverCard>
      //       )
      //     } else {
      //       return t("notArchived")
      //     }
      //   },
      // },
      {
        key: "createdBy",
        name: t("registeredBy"),
        render(owner) {
          return owner?.createdBy
        },
      },
      {
        key: "createdAt",
        name: t("registeredAt"),
        render(owner) {
          return dateFormat(owner?.createdAt)
        },
      },
      {
        key: "enabled",
        name: t("status"),
        render(owner) {
          return owner?.ownerStatus
        },
      },
    ],
  }

  function _parseConfig() {
    if (userInfo?.role?.key === "owner") {
      return {
        ...config,
        visibleColumn: config.visibleColumn.filter(
          (item) => item.key !== "pricePerMonth",
        ),
      }
    } else {
      return config
    }
  }
  return (
    <main className="max-w-7xl mx-auto py-6 px-4 lg:px-8">
      <LoadingOverlay visible={owners?.isFetching} />
      <motion.div
        className="hidden md:grid grid-cols-1 gap-5 sm:grid-cols-1 lg:grid-cols-3 mb-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <StatCard
          name={
            userInfo?.role?.key === "owner" ? t("totalUsers") : t("totalOwners")
          }
          icon={Users}
          value={ownerCount?.data?.count}
          color="#6366F1"
        />
        <StatCard
          name={
            userInfo?.role?.key === "owner"
              ? t("activeUsers")
              : t("activeOwners")
          }
          icon={User}
          value={activeOwnerCount?.data?.count}
          color="#8B5CF6"
        />
        <StatCard
          name={
            userInfo?.role?.key === "owner"
              ? t("inactiveUsers")
              : t("inactiveOwners")
          }
          icon={UserCircle}
          value={inactiveOwnerCount?.data?.count}
          color="#EC4899"
        />
        {/* <StatCard name='Total Revenue' icon={DollarSign} value='12.5%' color='#10B981' /> */}
      </motion.div>
      <EntityList
        viewMode={viewMode}
        title={
          userInfo?.role?.key === "owner"
            ? t("user", { count: 2 })
            : t("owner", { count: 2 })
        }
        total={owners?.data?.count}
        itemsLoading={owners.isFetching}
        config={_parseConfig()}
        showExport={true}
        items={data}
        initialPage={1}
        defaultPageSize={10}
        pageSize={[10, 15, 20]}
        collectionQuery={collection}
        onArchivedChecked={(e) =>
          setCollection({
            ...collection,
            withArchived: e,
          })
        }
        onPaginationChange={(skip: number, top: number) => {
          if (collection.skip !== skip || collection.top !== top) {
            setCollection({ ...collection, skip: skip, top: top })
          }
        }}
        onSearch={(data: any) => {
          setCollection({
            ...collection,
            search: data,
            searchFrom: ["name", "email", "phoneNumber"],
          })
        }}
        onFilterChange={(data: any) => {
          if (collection?.filter || data.length > 0) {
            setCollection({ ...collection, filter: data })
          }
        }}
        onOrder={(data: Order) =>
          setCollection({ ...collection, orderBy: [data] })
        }
        onExport={() => {
          openExport()
        }}
        onBulkActivate={(items) => {
          if (items && items.length > 0)
            bulkToggleStatus({
              ids: items?.map((item) => item.id) as string[],
              enabled: true,
            })
        }}
        onBulkDeactivate={(items) => {
          if (items && items.length > 0)
            bulkToggleStatus({
              ids: items?.map((item) => item.id) as string[],
              enabled: false,
            })
        }}
        bulkActionLoading={bulkToggleStatusResponse.isLoading}
      />

      <ExportModalComponent
        open={openExport}
        opened={openedExport}
        close={closeExport}
        filename={"owners.xlsx"}
        filteringVariables={[
          [
            {
              field: "enabled",
              value: true,
              name: t("active"),
              operator: "=",
            },
            {
              field: "enabled",
              value: false,
              name: t("inactive"),
              operator: "=",
            },
          ],
          [
            {
              field: "gender",
              value: "male",
              name: t("male"),
              operator: "=",
            },
            {
              field: "gender",
              value: "female",
              name: t("female"),
              operator: "=",
            },
          ],
        ]}
        exportFunction={exportOwners}
        filterOwnerBy={"id"}
      />

      {/* <ProductTable
      
      viewMode={viewMode}
        title={"Owners"}
        total={owners?.data?.count}
        // total={200}
        itemsLoading={owners.isFetching}
        config={config}
        showExport={false}
        header={<>
          <div className="h-14 dark:border-gray-500 dark:bg-gray-500 flex items-center justify-between p-2 border-0 border-b">
            <div className="h-full flex items-center space-x-4 font-semibold dark:text-white">
              {"Owners"}
            </div>
            {
              <div className="h-full  space-x-4 flex items-center">
                {viewMode == 'list' && (

                  <>
                    <Checkbox
                      size="sm"
                      onChange={(e) =>
                        setCollection({ ...collection, withArchived: e.currentTarget.checked ? true : false })

                      }
                      label={
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Show Archived
                        </span>
                      }
                    />

                    <Button
                      classNames={{ label: "flex space-x-2 text-white" }}
                      className="bg-primary dark:bg-dark_primary"
                      size="xs"
                      type="button"
                      onClick={(e) => openExport()}
                      variant="filled"
                    >
                      <span className="text-white">
                        <IconFileExport />
                      </span>
                      <span>Export</span>
                    </Button>

                  </>

                )}
                <Link
                  to={`/owner/new`}
                  state={{ id: location?.state?.id }}
                >
                  <Button
                    classNames={{ label: "flex space-x-2 text-white" }}
                    className="bg-primary dark:bg-dark_primary"
                    size="xs"
                    variant="filled"
                  >
                    <span className="text-white">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="fill-current h-4 text-white"
                        viewBox="0 0 24 24"
                      >
                        <path
                          fillRule="evenodd"
                          d="M11 2L11 11L2 11L2 13L11 13L11 22L13 22L13 13L22 13L22 11L13 11L13 2Z"
                        />
                      </svg>
                    </span>
                    <span>New</span>
                  </Button>
                </Link>

              </div>
            }
          </div>

        </>}
        items={data}
        initialPage={1}
        defaultPageSize={10}
        pageSize={[10, 15, 20]}
        collectionQuery={collection}
        onArchivedChecked={(e) =>
          setCollection({
            ...collection,
            withArchived: e,
          })
        }
        onPaginationChange={(skip: number, top: number) => {
          if (collection.skip !== skip || collection.top !== top) {
            setCollection({ ...collection, skip: skip, top: top });
          }
        }}
        onSearch={(data: any) => {
          setCollection({
            ...collection,
            search: data,
            searchFrom: ["name", "email", "phoneNumber"],
          });
        }}
        onFilterChange={(data: any) => {
          if (collection?.filter || data.length > 0) {
            setCollection({ ...collection, filter: data });
          }
        }}
        onOrder={(data: Order) =>
          setCollection({ ...collection, orderBy: [data] })
        }
      /> */}
    </main>
  )
}
